import { z } from 'zod';
import { AlertSchema } from './alerts';
import { LogLineSchema, LogLevelSchema } from './logs';
import { ProcessSummarySchema } from './process';
import { MetricSampleSchema } from './system';

export const API_PREFIX = '/api/v1';
export const STREAM_PATH = `${API_PREFIX}/stream`;

/** Messages the bridge pushes to the app. */
export const ServerMessageSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('hello'),
    payload: z.object({
      bridgeVersion: z.string(),
      serverTime: z.number(),
      /** How often `status` frames are sent, so the client can size its buffers. */
      statusIntervalMs: z.number(),
    }),
  }),
  z.object({
    type: z.literal('status'),
    payload: z.object({
      processes: z.array(ProcessSummarySchema),
      serverSample: MetricSampleSchema,
    }),
  }),
  z.object({ type: z.literal('log'), payload: LogLineSchema }),
  z.object({ type: z.literal('alert'), payload: AlertSchema }),
  z.object({
    type: z.literal('action'),
    payload: z.object({
      action: z.string(),
      processName: z.string(),
      ok: z.boolean(),
      message: z.string(),
    }),
  }),
  z.object({
    type: z.literal('error'),
    payload: z.object({ message: z.string() }),
  }),
]);
export type ServerMessage = z.infer<typeof ServerMessageSchema>;

/**
 * Messages the app sends up. Log filtering happens on the server so a phone on
 * mobile data is not forced to receive every line from every process just to
 * discard most of them.
 */
export const ClientMessageSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('subscribe_logs'),
    payload: z.object({
      /** Empty means all processes. */
      processIds: z.array(z.number()),
      levels: z.array(LogLevelSchema),
    }),
  }),
  z.object({ type: z.literal('unsubscribe_logs') }),
  z.object({ type: z.literal('ping') }),
]);
export type ClientMessage = z.infer<typeof ClientMessageSchema>;

export const PROCESS_ACTIONS = [
  'restart',
  'reload',
  'stop',
  'start',
  'delete',
  'flush',
] as const;
export type ProcessAction = (typeof PROCESS_ACTIONS)[number];

/** Actions that can lose work or take a service down; the app confirms these. */
export const DESTRUCTIVE_ACTIONS: ReadonlySet<ProcessAction> = new Set([
  'stop',
  'delete',
]);
